import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from './config';
import SheetsManagement from './components/SheetsManagement';
import Accounts from './components/Accounts';
import Search from './components/Search';
import Ledger from './components/Ledger';
import Transfer from './components/Transfer';
import Activity from './components/Activity';
import EomReport from './components/EomReport';
import Login from './components/Login';
import './App.css';

const brandLogoSrc = `${process.env.PUBLIC_URL}/brand/morgan-hill-solicitors-logo.png`;

// Set up axios interceptor to include token in requests
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 errors (unauthorized)
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't handle 401 for auth endpoints (login/register) - let them handle their own errors
    const requestUrl = error.config?.url || '';
    const isAuthEndpoint = requestUrl.includes('/api/auth/login') ||
      requestUrl.includes('/api/auth/register');

    if (error.response?.status === 401 && !isAuthEndpoint) {
      // Only clear auth and reload if we have a token (meaning it expired)
      // If no token, it's likely a login attempt that failed, so don't reload
      const token = localStorage.getItem('token');
      if (token) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        // Reload to show login page
        window.location.reload();
      }
    }
    return Promise.reject(error);
  }
);

function App() {
  const [activeView, setActiveView] = useState('sheets');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (token && storedUser) {
      // Verify token is still valid
      axios
        .get(`${API_URL}/api/auth/me`)
        .then((response) => {
          setUser(response.data.user);
          setIsAuthenticated(true);
        })
        .catch(() => {
          // Token invalid, clear storage
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setIsAuthenticated(false);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 1024) {
        setMobileNavOpen(false);
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!mobileNavOpen) {
      return undefined;
    }
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setMobileNavOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileNavOpen]);

  useEffect(() => {
    if (!mobileNavOpen) {
      document.body.style.overflow = '';
      return undefined;
    }
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileNavOpen]);

  const goToView = (view) => {
    setActiveView(view);
    setMobileNavOpen(false);
  };

  const handleLogin = (userData, token) => {
    setUser(userData);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
  };

  if (loading) {
    return (
      <div className="app-loading-screen">
        <img className="app-loading-brand" src={brandLogoSrc} alt="" width={200} height={56} decoding="async" />
        <div className="app-loading-text">Loading…</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div className="App">
      <div className="app-container">
        <div className="header">
          <div className="header-inner">
            <button
              type="button"
              className="nav-menu-toggle"
              aria-label={mobileNavOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={mobileNavOpen}
              aria-controls="app-sidebar"
              onClick={() => setMobileNavOpen((open) => !open)}
            >
              <span className="nav-menu-toggle-bar" aria-hidden />
              <span className="nav-menu-toggle-bar" aria-hidden />
              <span className="nav-menu-toggle-bar" aria-hidden />
            </button>
            <div className="header-brand">
              <h1 className="sr-only">Morgan Hill Solicitors — client ledger</h1>
              <img
                className="header-logo"
                src={brandLogoSrc}
                alt="Morgan Hill Solicitors"
                width={220}
                height={52}
                decoding="async"
              />
              <p className="header-subtitle">Client ledger &amp; matter accounting</p>
            </div>
            <div className="header-toolbar">
              <div className="header-user-meta">
                <div style={{ fontSize: '14px', opacity: 0.9 }}>
                  {user?.fullName || user?.username}
                </div>
                <div style={{ fontSize: '12px', opacity: 0.7 }}>{user?.email}</div>
              </div>
              <button type="button" onClick={handleLogout} className="header-logout-btn">
                Log out
              </button>
            </div>
          </div>
        </div>

        <button
          type="button"
          className={`sidebar-backdrop ${mobileNavOpen ? 'is-visible' : ''}`}
          aria-label="Close menu"
          aria-hidden={!mobileNavOpen}
          tabIndex={mobileNavOpen ? 0 : -1}
          onClick={() => setMobileNavOpen(false)}
        />

        <div className="main-layout">
          <div
            id="app-sidebar"
            className={`sidebar ${mobileNavOpen ? 'sidebar--open' : ''}`}
          >
            <nav className="sidebar-nav" aria-label="Main">
              <button
                type="button"
                className={`sidebar-item ${activeView === 'sheets' ? 'active' : ''}`}
                onClick={() => goToView('sheets')}
              >
                <span className="sidebar-icon">📁</span>
                <span className="sidebar-text">Sheets</span>
              </button>
              <button
                type="button"
                className={`sidebar-item ${activeView === 'accounts' ? 'active' : ''}`}
                onClick={() => goToView('accounts')}
              >
                <span className="sidebar-icon">👥</span>
                <span className="sidebar-text">Accounts</span>
              </button>
              <button
                type="button"
                className={`sidebar-item ${activeView === 'search' ? 'active' : ''}`}
                onClick={() => goToView('search')}
              >
                <span className="sidebar-icon">🔍</span>
                <span className="sidebar-text">Search</span>
              </button>
              <button
                type="button"
                className={`sidebar-item ${activeView === 'ledger' ? 'active' : ''}`}
                onClick={() => goToView('ledger')}
              >
                <span className="sidebar-icon">📊</span>
                <span className="sidebar-text">Ledger</span>
              </button>
              <button
                type="button"
                className={`sidebar-item ${activeView === 'transfer' ? 'active' : ''}`}
                onClick={() => goToView('transfer')}
              >
                <span className="sidebar-icon">⇄</span>
                <span className="sidebar-text">Transfer</span>
              </button>
              <button
                type="button"
                className={`sidebar-item ${activeView === 'activity' ? 'active' : ''}`}
                onClick={() => goToView('activity')}
              >
                <span className="sidebar-icon">📋</span>
                <span className="sidebar-text">Activity</span>
              </button>
              <button
                type="button"
                className={`sidebar-item ${activeView === 'eom' ? 'active' : ''}`}
                onClick={() => goToView('eom')}
              >
                <span className="sidebar-icon">📆</span>
                <span className="sidebar-text">Month-end report</span>
              </button>
            </nav>
          </div>

          {/* Main Content */}
          <div className="main-content">
            {activeView === 'sheets' && <SheetsManagement />}
            {activeView === 'accounts' && <Accounts />}
            {activeView === 'search' && <Search />}
            {activeView === 'ledger' && <Ledger />}
            {activeView === 'transfer' && <Transfer />}
            {activeView === 'activity' && <Activity />}
            {activeView === 'eom' && <EomReport />}
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;

